import Vue from 'vue'
import { createDocument, getSchema } from '@tiptap/core'
import { Editor } from '@tiptap/vue-2'
import { Node as ProseMirrorNode, Schema, DOMSerializer } from 'prosemirror-model'
import { EditorState, Plugin } from 'prosemirror-state'
import { buildSchemaEditorExtensions, createEditor, SCHEMA_VERSION } from './tiptap-editor-creator'
import { normalizeVariablePlaceholderDom } from './variable-placeholder-dom'
import { EMPTY_EDITOR_CLASS, EMPTY_NODE_CLASS } from './tiptap-placeholder'
import { clearLoadedEmbeds, createEmbedCard, EMBED_WRAPPER_CLASS } from './embed-click-to-load'
import { createEmbedPrintCard } from './embed-print-card'
import { isStaticFirstRender } from './feature-flags'
import { interactiveLoadState, kickInteractiveEditorExtensionsRetry } from './tiptap-editor-interactive-loader'
import { isExportMode } from './canvas-render-mode'
import i18n from '@/util/config/i18n'
import { useSpaceVariableStore } from '@/modules/space/stores/SpaceVariableStore'
import type SpaceVariable from '@/modules/space/models/SpaceVariable'
import { useSpaceStore } from '@/modules/space/stores/SpaceStore'
import { useAppStore } from '@/modules/global/stores/AppStore'
import { usePermissionStore } from '@/modules/auth/stores/PermissionStore'
import { useUserStore } from '@/modules/user/stores/UserStore'

/**
 * Renders stored rich-text HTML without a per-block TipTap editor, through the same schema and parser the editors
 * use. Anything the schema does not model (scripts, event handlers, unknown tags) is dropped by the parse.
 *
 * Content with variables goes through a shared render editor instead: the variable plugin rewrites the document
 * on load from store state (canonical `{{ name }}` text, merged wrappers, assign callouts while editable), and
 * running the plugin itself is the only way to show exactly what a cell editor would.
 */

// ProseMirror's view adds this to empty text blocks so they keep one line of height
const TRAILING_BREAK = '<br class="ProseMirror-trailingBreak">'
/** What an empty editor renders; parents compare against it to decide on a placeholder. */
export const EMPTY_STATIC_HTML = `<p>${TRAILING_BREAK}</p>`

const MAX_ENTRIES = 2000
const MAX_PLACEHOLDER_ENTRIES = 100
const MAX_CHARS = 5_000_000
const SAFE_PLAIN = /^[^<>&]*$/
// The whitespace ProseMirror's parser collapses in its default (non-preserving) mode
const PM_WHITESPACE = /[ \t\r\n\f]+/g
const URL_SCHEME = /^([a-z][a-z0-9+.-]*):/i
const SAFE_SCHEMES = ['http', 'https', 'mailto', 'tel']
const SAFE_DATA_IMAGE = /^data:image\/(png|gif|jpe?g|webp|avif);/i
// Browsers ignore these inside a URL scheme, so `java\tscript:` must not slip past the check
// eslint-disable-next-line no-control-regex
const URL_IGNORED_CHARS = /[\u0000- \u007f-\u009f]/g
// Variable display depends on store state applied by the editor's variable plugin
const VARIABLE_MARKERS = /variable-placeholder|variable-wrapper|\{\{/
const HEADING_LEVELS = [1, 2, 3, 4, 5, 6]

/** How the content would be shown by the editor it stands in for. Only matters for content with variables. */
export type StaticRenderOptions = {
  /** The editor's `variableScopeId` (set for template content) */
  variableScopeId?: string
}

/**
 * What an iframe becomes: a print card in the export, a click-to-load card (flag on), or itself. The same rules as
 * isClickToLoadEnabled and isEmbedPrintCardEnabled, decided once per render: this runs for every static cell.
 */
type EmbedDisplay = 'card' | 'print' | null

function embedDisplay(): EmbedDisplay {
  if (isExportMode()) return 'print'
  return isStaticFirstRender() ? 'card' : null
}

type RenderEditor = { editor: Editor; host: Vue; plugins: readonly Plugin[]; serializer: DOMSerializer }

let schema: Schema | null = null
let extensionNames: readonly string[] = []
let serializer: DOMSerializer | null = null
const textblockTags = new Set<string>()
const cache = new Map<string, string>()
let cachedChars = 0
// Empty static block markup by placeholder text; holds no content, so it outlives space leave and logout
const emptyWithPlaceholder = new Map<string, string>()
// One per variable scope, created on the first render of content with variables
const renderEditors = new Map<string, RenderEditor>()

function ensureSchema(): void {
  if (schema) return
  // Every editor shares this superset schema, whatever its host turns on; building it creates no editor
  const host = new Vue()
  const extensions = buildSchemaEditorExtensions({ parent: host, enableSlashCommands: false })
  schema = getSchema(extensions)
  extensionNames = Object.freeze(extensions.map(extension => extension.name))
  // Only editors use the parent (for popups); the schema keeps no reference to it
  host.$destroy()
  serializer = DOMSerializer.fromSchema(schema)
  Object.values(schema.nodes)
    .filter(type => type.isTextblock)
    .forEach(type => {
      // A heading's tag depends on its level
      const variants = type.spec.attrs?.level ? HEADING_LEVELS.map(level => ({ level })) : [undefined]
      variants.forEach(attrs => {
        const node = type.createAndFill(attrs)
        const dom = node && serializer!.serializeNode(node)
        if (dom instanceof HTMLElement) textblockTags.add(dom.tagName)
      })
    })
}

/** The shared superset schema every editor and the static renderer parse against (invariant 1.3a). */
export function getStaticRenderSchema(): Schema {
  ensureSchema()
  return schema!
}

/** The names of the extensions the shared schema is built from (some, like text alignment, add no node or mark) */
export function getStaticRenderExtensionNames(): readonly string[] {
  ensureSchema()
  return extensionNames
}

export function hasVariableContent(html: string | null | undefined): boolean {
  return !!html && VARIABLE_MARKERS.test(html)
}

export function isEmptyStaticHtml(html: string | null | undefined): boolean {
  return !html || html === EMPTY_STATIC_HTML
}

/**
 * The empty form with the Placeholder extension's decoration, so one CSS rule shows the placeholder on static and
 * live blocks alike. The attribute sits on the paragraph because `attr()` only reads the pseudo-element's own element.
 */
export function emptyStaticHtmlWithPlaceholder(text: string): string {
  let html = emptyWithPlaceholder.get(text)
  if (html === undefined) {
    // Built through the DOM so the attribute value is escaped
    const p = document.createElement('p')
    p.className = `${EMPTY_NODE_CLASS} ${EMPTY_EDITOR_CLASS}`
    p.setAttribute('data-placeholder', text)
    p.innerHTML = TRAILING_BREAK
    html = p.outerHTML
    // Placeholder texts are a handful of i18n strings; the bound only guards against unexpected callers
    if (emptyWithPlaceholder.size >= MAX_PLACEHOLDER_ENTRIES) emptyWithPlaceholder.clear()
    emptyWithPlaceholder.set(text, html)
  }
  return html
}

function isSafeUrl(attr: string, value: string): boolean {
  const normalized = value.replace(URL_IGNORED_CHARS, '')
  const scheme = URL_SCHEME.exec(normalized)?.[1].toLowerCase()
  // No scheme means a relative URL, which the editor renders as-is too
  if (!scheme) return true
  if (SAFE_SCHEMES.includes(scheme)) return true
  return attr === 'src' && SAFE_DATA_IMAGE.test(normalized)
}

function sanitizeUrls(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>('[href], [src]').forEach(el => {
    ;['href', 'src'].forEach(attr => {
      const value = el.getAttribute(attr)
      if (value !== null && !isSafeUrl(attr, value)) el.removeAttribute(attr)
    })
  })
}

/**
 * Adds the break ProseMirror's view appends to a text block that is empty or does not end in text (a hard break,
 * a variable, an anchor), so it keeps the height the editor gives it. Decided from the document, like the view does;
 * serialized text blocks come out in document order.
 */
function addTrailingBreaks(root: HTMLElement, doc: ProseMirrorNode): void {
  const needsBreak: boolean[] = []
  doc.descendants(node => {
    if (!node.isTextblock) return true
    const last = node.lastChild
    needsBreak.push(!last || !last.isText || (last.text || '').endsWith('\n'))
    return false
  })
  const blocks = textblocksInOrder(root)
  // Another node rendering a text block's tag would shift the pairing; fall back to what the DOM shows
  const paired = blocks.length === needsBreak.length
  blocks.forEach((block, i) => {
    const add = paired ? needsBreak[i] : !block.lastChild || block.lastChild.nodeName === 'BR'
    if (add) block.insertAdjacentHTML('beforeend', TRAILING_BREAK)
  })
}

/** Text block elements in document order (a selector list is not reliably ordered in every DOM implementation). */
function textblocksInOrder(root: Element, found: Element[] = []): Element[] {
  Array.from(root.children).forEach(child => {
    // Text blocks never nest, so their content needs no walk
    if (textblockTags.has(child.tagName)) found.push(child)
    else textblocksInOrder(child, found)
  })
  return found
}

function remember(key: string, out: string): void {
  if (key.length + out.length > MAX_CHARS / 10) return
  cache.set(key, out)
  cachedChars += key.length + out.length
  while (cache.size > MAX_ENTRIES || cachedChars > MAX_CHARS) {
    const [oldKey, oldOut] = cache.entries().next().value as [string, string]
    cache.delete(oldKey)
    cachedChars -= oldKey.length + oldOut.length
  }
}

function serializeDoc(
  doc: ProseMirrorNode,
  docSerializer: DOMSerializer,
  withVariables: boolean,
  embeds: EmbedDisplay
): string {
  const first = doc.firstChild
  // Only the default empty document maps to the placeholder form; other empty shapes keep their layout
  if (doc.childCount === 1 && first?.type.name === 'paragraph' && first.content.size === 0) return EMPTY_STATIC_HTML
  const out = document.createElement('div')
  out.appendChild(docSerializer.serializeFragment(doc.content))
  // What TextEditor does to a live editor's DOM after each transaction
  if (withVariables) normalizeVariablePlaceholderDom(out)
  sanitizeUrls(out)
  disableTaskCheckboxes(out)
  if (embeds === 'print') replaceIframesWithPrintCards(out)
  else if (embeds === 'card') replaceIframes(out)
  addTrailingBreaks(out, doc)
  return out.innerHTML
}

/**
 * Static HTML has no task-item node view to save or revert a toggle, so its checkboxes only show their state:
 * disabled, they leave the tab order and screen readers still announce checked / unchecked.
 */
function disableTaskCheckboxes(root: HTMLElement): void {
  root.querySelectorAll('input[type="checkbox"]').forEach(input => {
    input.setAttribute('disabled', '')
    // Named after its item, as the live node view does; an empty item stays unnamed (no locale in the cache key)
    const label = input.closest('li')?.querySelector('div > p')?.textContent?.trim()
    if (label) input.setAttribute('aria-label', label)
  })
}

/** Click-to-load: an iframe becomes a card (TextEditor loads it on click), so cached output never holds a live one. */
function replaceIframes(root: HTMLElement): void {
  root.querySelectorAll('iframe').forEach(iframe => {
    iframe.parentElement?.classList.add(EMBED_WRAPPER_CLASS)
    iframe.replaceWith(createEmbedCard(document, iframe.getAttribute('src')))
  })
}

/** Export: an iframe becomes a print card, whose QR code TextEditor adds (embed-print-card.ts). */
function replaceIframesWithPrintCards(root: HTMLElement): void {
  root
    .querySelectorAll('iframe')
    .forEach(iframe => iframe.replaceWith(createEmbedPrintCard(document, iframe.getAttribute('src'))))
}

/** Store state the variable plugin reads while parsing and syncing; a change must miss the cache. */
function variableDisplayState(options: StaticRenderOptions): string {
  const parts: string[] = [
    options.variableScopeId || '',
    useSpaceStore().currentSpaceId || '',
    useAppStore().isInPreviewMode ? 'p' : '',
    usePermissionStore().isExternalUser() ? 'x' : ''
  ]
  const user = useUserStore().currentUser
  parts.push(user?.id || '', user?.email || '', variablesHash())
  return hashString(parts.join('|'))
}

let variablesHashMemo: { variables: Map<string, SpaceVariable[]>; revision: number; hash: string } | null = null

/**
 * The variables' share of the key, hashed once per store state instead of once per rendered cell. Every write in
 * SpaceVariableStore either replaces the Map or bumps `revision` (a change inside the Map, which Vue 2 cannot see),
 * so the same Map at the same revision holds the same variables.
 */
function variablesHash(): string {
  const variableStore = useSpaceVariableStore()
  // Both are read on every call: they are what re-renders static content (the caller's reactive dependencies)
  const variables = variableStore.spaceVariables
  const revision = variableStore.revision
  const memo = variablesHashMemo
  if (memo && memo.variables === variables && memo.revision === revision) return memo.hash
  const parts: string[] = []
  // The plugin looks variables up by id across every loaded scope, so all of them count
  variables.forEach((scopeVariables, scopeId) => {
    scopeVariables.forEach(v =>
      parts.push(
        `${scopeId}/${v.id}/${v.variableName}/${v.defaultValue || ''}/${v.recipientValue || ''}/` +
          `${v.assignedToRecipientId || ''}/${v.assignedToRecipientName || ''}`
      )
    )
  })
  const hash = hashString(parts.join('|'))
  variablesHashMemo = { variables, revision, hash }
  return hash
}

/** Two 32-bit multiplicative hashes (FNV-1a and a variant with another prime); keys stay short however many variables a space has. */
function hashString(value: string): string {
  let a = 0x811c9dc5
  let b = 0x01000193
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i)
    a = Math.imul(a ^ code, 0x01000193)
    b = Math.imul(b ^ code, 0x5bd1e995)
  }
  return `${(a >>> 0).toString(36)}${(b >>> 0).toString(36)}`
}

function getRenderEditor(scopeId: string | undefined): RenderEditor {
  const key = scopeId || ''
  let entry = renderEditors.get(key)
  if (!entry) {
    const host = new Vue()
    const editor = createEditor({
      content: '',
      parent: host,
      enableSlashCommands: false,
      variableScopeId: scopeId,
      diagnosticsKind: 'static-render'
    })
    entry = { editor, host, plugins: editor.state.plugins, serializer: DOMSerializer.fromSchema(editor.schema) }
    renderEditors.set(key, entry)
    park(entry)
  }
  return entry
}

/** A state without plugins destroys the plugin views, so an idle render editor runs no variable-sync interval. */
function park({ editor }: RenderEditor): void {
  editor.view.updateState(EditorState.create({ schema: editor.schema }))
}

function dropRenderEditor(scopeId: string | undefined): void {
  const key = scopeId || ''
  const entry = renderEditors.get(key)
  renderEditors.delete(key)
  if (!entry) return
  try {
    entry.editor.destroy()
  } finally {
    entry.host.$destroy()
  }
}

function renderWithVariablePlugin(html: string, options: StaticRenderOptions, embeds: EmbedDisplay): string {
  const entry = getRenderEditor(options.variableScopeId)
  const { editor } = entry
  // Stays editable like a new cell editor: its variable plugin syncs at creation, before TextEditor applies
  // readonly, so read-only cells show assign callouts too.
  // A new plugins array makes ProseMirror create fresh plugin views, so the variable plugin runs its load-time
  // sync against this content with no state left from the previous render
  try {
    const doc = createDocument(html, editor.schema, {})
    editor.view.updateState(EditorState.create({ doc, plugins: [...entry.plugins] }))
    return serializeDoc(editor.state.doc, entry.serializer, true, embeds)
  } finally {
    try {
      park(entry)
    } catch {
      // A render editor that cannot be parked might keep its interval running; replace it on the next render
      dropRenderEditor(options.variableScopeId)
    }
  }
}

export function renderStaticHtml(html: string | null | undefined, options: StaticRenderOptions = {}): string {
  if (!html) return EMPTY_STATIC_HTML
  const withVariables = hasVariableContent(html)
  // No tags or entities: already safe as HTML text. Collapse whitespace the way the editor's parse does.
  if (!withVariables && SAFE_PLAIN.test(html)) {
    const text = html.replace(PM_WHITESPACE, ' ').replace(/^ | $/g, '')
    return text ? `<p>${text}</p>` : EMPTY_STATIC_HTML
  }

  // Embed and print cards carry translated text, so which one shows and the locale are part of the key
  const embeds = embedDisplay()
  const embedKey = embeds ? `${embeds}-${i18n.locale}` : ''
  const key = `${SCHEMA_VERSION}:${embedKey}:${withVariables ? variableDisplayState(options) : ''}:${html}`
  const hit = cache.get(key)
  if (hit !== undefined) {
    cache.delete(key)
    cache.set(key, hit)
    return hit
  }

  ensureSchema()
  let result: string
  if (withVariables && !interactiveLoadState.loaded) {
    // The variable plugin needs the interactive chunk. Reading the reactive flag makes a computed render again when it
    // arrives; until then show the content without variable display and keep that out of the cache.
    kickInteractiveEditorExtensionsRetry()
    return renderThroughSchema(html, embeds)
  }
  if (withVariables) {
    try {
      result = renderWithVariablePlugin(html, options, embeds)
    } catch (error) {
      // One cell must not break the table: show it without variable display, and do not cache that
      console.warn('Static variable render failed:', error instanceof Error ? error.message : 'unknown error')
      return renderThroughSchema(html, embeds)
    }
  } else {
    result = renderThroughSchema(html, embeds)
  }
  remember(key, result)
  return result
}

// The same parse an editor runs on its initial content; DOMParser documents are inert (no scripts, no loads)
function renderThroughSchema(html: string, embeds: EmbedDisplay): string {
  return serializeDoc(createDocument(html, schema!, {}), serializer!, false, embeds)
}

const clearHooks = new Set<() => void>()

/** Other caches keyed by document content register here, so they are dropped together with the render cache. */
export function onStaticRenderCacheClear(clear: () => void): void {
  clearHooks.add(clear)
}

/**
 * Drops cached renders, the render editors and which embeds were loaded; on space leave and logout, so content never
 * outlives its page. Never throws: callers run it inside logout and route guards, which must complete.
 */
export function clearStaticRenderCache(): void {
  cache.clear()
  cachedChars = 0
  variablesHashMemo = null
  clearLoadedEmbeds()
  clearHooks.forEach(clear => {
    try {
      clear()
    } catch (error) {
      console.warn('Static render cache hook failed:', error instanceof Error ? error.message : 'unknown error')
    }
  })
  Array.from(renderEditors.keys()).forEach(key => {
    try {
      dropRenderEditor(key || undefined)
    } catch (error) {
      console.warn('Static render editor cleanup failed:', error instanceof Error ? error.message : 'unknown error')
    }
  })
}
